import {useCallback, useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import type {DataSectionInitialState, DataSectionSelector} from './typedef';
import type {ActionCreatorWithPayload} from '@reduxjs/toolkit';
import type {Error} from 'src/openapi/admin/models/Error';


type Props<Data, Validation> = {
    selector: DataSectionSelector<Data>,
    actions: {
        updateData: ActionCreatorWithPayload<Data>,
        clearData: ActionCreatorWithPayload<void>,
    },
    getAction: () => Promise<{
        type: string,
        payload: {
            data: Data,
            success: true,
            error: false,
        } | {
            data: Error,
            success: false,
            error: true,
        },
    }>,
    editValidation?: (data: Data, validationData?: Validation) => void,
    shouldLoadData?: boolean,
};

export const useGetDataSection = <Data, Validation>(params: Props<Data, Validation>) => {
    const {
        selector,
        actions,
        getAction,
        editValidation,
        shouldLoadData = true,
    } = params;

    const {isLoading, data, loadingError}: DataSectionInitialState<Data> = useSelector(selector);
    const dispatch = useDispatch();

    useEffect(() => {
        return () => {
            dispatch(actions.clearData());
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const loadData = useCallback(async () => {
        await getAction();
    }, [getAction]);

    const setData = useCallback((updatedData: Data, validation?: Validation) => {
        dispatch(actions.updateData(updatedData));

        if (editValidation) {
            editValidation(updatedData, validation);
        }
    }, [editValidation, actions, dispatch]);

    useEffect(() => {
        if (shouldLoadData) {
            loadData();
        }
    }, [shouldLoadData, loadData]);

    return {
        isLoading,
        loadingError,
        data,
        setData,
        loadData,
    };
};
